import { useStore } from '@doltech/core/lib/entity/store';
import { FileModel } from '@doltech/ui/lib/figma/DolEditor/elements/BlogEditor/model/file-upload-model';
import { Author } from '@doltech/ui/lib/figma/EditSEOInfo/constants';
import { showToast } from '@doltech/ui/lib/figma/Toast';
import { AvailableForEnum, ResourceStatusEnum } from '@doltech/utils/lib/constants';
import { getTableOfContentFromEditor } from '@doltech/utils/lib/getTableOfContentFromEditor';
import { isNotEmpty } from '@doltech/utils/lib/text';
import { isNil } from 'lodash';
import { Instance, types, types as t } from 'mobx-state-tree';
import * as React from 'react';
import { Node } from 'slate';

import { BLOG_SECTION_PREFIX, BlogContentGroup } from '../blog-management-constant';
import { BlogType } from '../enum/BlogTypeEnum';
import { convertData, stringlifyData } from './admin-create-blog.util';
import { BlogVocabList, BlogVocabType } from './blog-vocab.model';

const SPECIAL_CHAR = '--**--';

export const AdminCreateBlogStore = t
  .model('AdminCreateBlogStore', {
    id: t.maybeNull<any>(t.string),
    blogId: t.maybeNull<any>(t.string),
    thumbnail: t.maybe(FileModel),
    cover: t.maybe(FileModel),
    name: t.maybeNull<any>(t.string),
    title: t.maybeNull<any>(t.string),
    tocTitle: t.maybeNull<any>(t.string),
    blogContentGroup: types.maybeNull(
      types.enumeration<BlogContentGroup>('BlogContentGroup', Object.values(BlogContentGroup))
    ),
    blogType: types.maybeNull(types.enumeration<BlogType>('BlogType', Object.values(BlogType))),
    singleVocab: t.maybeNull(BlogVocabList),
    vocabList: t.maybeNull(BlogVocabList),
    author: t.optional<any>(t.string, Author.DOL_IELTS_DINH_LUC),
    blogLength: t.optional<any>(t.string, 'TWO'),
    header: t.maybeNull<any>(t.string),
    tableOfContent: t.maybeNull<any>(t.string),
    tableOfContentHidden: t.optional(t.array(t.string), []),
    body: t.maybeNull<any>(t.string),
    status: t.maybeNull(
      types.enumeration<ResourceStatusEnum>('ResourceStatusEnum', Object.values(ResourceStatusEnum))
    ),
    createdAt: t.maybeNull(t.string),
    availableFor: t.optional(
      types.enumeration<AvailableForEnum>('AvailableForEnum', Object.values(AvailableForEnum)),
      AvailableForEnum.ALL
    ),
    isTocShown: t.optional(t.boolean, true),
  })
  .actions((self) => ({
    updateBlogContentGroup(type) {
      self.blogContentGroup = type;
    },
    updateBlogTypes({ blogContentGroup, blogType }) {
      self.blogContentGroup = blogContentGroup;
      self.blogType = blogType;
    },
    updateId(id) {
      self.id = id;
    },
    updateTocShown(newValue: boolean) {
      self.isTocShown = newValue;
    },
    updateHeader(header) {
      self.header = header;
    },
    updateTableOfContent(tableOfContent) {
      self.tableOfContent = tableOfContent;
    },
    updateBody(body) {
      self.body = body;
    },
    addBlogVocab(id) {
      if (!self.singleVocab) {
        self.singleVocab = BlogVocabList.create({
          blogVocabs: [],
          blogVocabType: BlogVocabType.SINGLE,
        });
      }
      self.singleVocab.blogVocabs.push({ id });
    },
    updateBlogVocab(id, data) {
      const index = self.singleVocab.blogVocabs.findIndex((vocab) => vocab.id === id);
      if (index > -1) {
        self.singleVocab.blogVocabs[index] = data;
      }
    },
    updateListVocab(data) {
      if (!self.vocabList) {
        self.vocabList = BlogVocabList.create({
          blogVocabs: [],
          blogVocabType: BlogVocabType.LIST,
        });
      }
      self.vocabList.blogVocabs.replace(data);
    },
    removeListVocab(data) {
      self.vocabList.blogVocabs.clear();
    },
    removeVocab(id) {
      const found = self.singleVocab.blogVocabs.find((vocab) => vocab.id === id);
      if (found) {
        self.singleVocab.blogVocabs.remove(found);
      }
    },

    setAvailableFor(setAvailableFor) {
      self.availableFor = setAvailableFor;
    },
    updateTocTitle(tocTitle) {
      self.tocTitle = tocTitle;
    },
    updateThumbnail(data) {
      const { caption, alt } = data;
      self.thumbnail.caption = caption;
      self.thumbnail.alt = alt;
    },
    updateHidden(id) {
      if (self.tableOfContentHidden.includes(id)) {
        self.tableOfContentHidden.remove(id);
      } else {
        self.tableOfContentHidden.push(id);
      }
    },
    initData(data) {
      const {
        id,
        thumbnail,
        cover,
        availableFor,
        status,
        header,
        singleVocab,
        vocabList,
        tableOfContent,
        body,
        blogLength,
        blogContentGroup,
        name,
        blogId,
        blogType,
        title,
        tocTitle,
        isTocShown,
        tableOfContentHidden,
        createdAt,
      } = data;
      self.id = id;
      self.blogId = blogId;
      self.cover = cover;
      self.thumbnail = thumbnail;
      self.singleVocab = singleVocab;
      self.vocabList = vocabList;
      self.availableFor = availableFor;
      self.status = status;
      self.header = header;
      self.blogLength = blogLength;
      self.tableOfContent = tableOfContent;
      self.tableOfContentHidden = tableOfContentHidden;
      self.body = body;
      self.blogContentGroup = blogContentGroup;
      self.blogType = blogType;
      self.name = name;
      self.title = title;
      self.tocTitle = tocTitle;
      self.createdAt = createdAt;
      self.isTocShown = isNil(isTocShown) ? true : isTocShown;
    },
    loadDataFromForm(data) {
      const { thumbnail, name, blogLength, blogId } = data;
      self.thumbnail = thumbnail;
      self.name = name;
      self.blogLength = blogLength;
      self.blogId = blogId;
    },
  }))
  .views((self) => ({
    get tableOfContentForEditor() {
      if (self.tableOfContent) {
        return JSON.parse(self.tableOfContent);
      }
      return undefined;
    },
    prepareData() {
      const header = JSON.parse(self.header)
        ?.map((n) => Node.string(n))
        .join(SPECIAL_CHAR);
      const title = header?.split(SPECIAL_CHAR)[0];
      const description = header?.split(SPECIAL_CHAR).slice(1).join(' ').replace(SPECIAL_CHAR, '');
      return {
        ...self,
        title,
        description,
      };
    },
    get isContentValid() {
      return (
        isNotEmpty(stringlifyData(self.header)) &&
        isNotEmpty(self.tableOfContent) &&
        isNotEmpty(stringlifyData(self.body))
      );
    },

    tableOfContentFromEditor(editor) {
      const onError = () => {
        showToast({
          icon: 'error',
          title: 'Invalid Data Format',
          subTitle: 'Hiện tại có H3 đang không nằm trong H2 nào. Vui lòng check lại',
        });
      };
      return getTableOfContentFromEditor(editor, onError);
    },
    get tableOfContentForView() {
      const data = [];
      const toc = JSON.parse(self.tableOfContent);
      convertData(toc)?.forEach((item, index) => {
        const contentItem = {
          id: `${BLOG_SECTION_PREFIX}${index + 1}`,
          item,
          sub: undefined,
        };
        item.h3Child?.forEach((cItem, cIndex) => {
          const subId = `${BLOG_SECTION_PREFIX}${index + 1}-${cIndex + 1}`;
          if (!self.tableOfContentHidden.includes(subId)) {
            if (!contentItem.sub) {
              contentItem.sub = [];
            }
            contentItem.sub.push({
              id: subId,
              item: cItem,
            });
          }
        });
        data.push(contentItem);
      });
      return data;
    },
  }));

export interface AdminCreateBlogStoreInstance extends Instance<typeof AdminCreateBlogStore> {}

export const AdminBlogStoreContext = React.createContext(null);

export const useAdminBlogStore = () => {
  const store = useStore<{ blog: AdminCreateBlogStoreInstance }>(AdminBlogStoreContext);
  return store?.blog;
};
